import Breadcrumbs from '@/components/Breadcrumbs';
import AlertBanner from '@/components/common/AlertBanner';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import useRoutes from '@/hooks/useRoutes';
import AppLayout from '@/layouts/AppLayout';
import { ClassData, Course } from '@/types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import AmaSessionsCard from './CourseDetail/Partials/AmaSessionsCard';
import CourseHeader from './CourseDetail/Partials/CourseHeader';
import ProgressCard from './CourseDetail/Partials/ProgressCard';
import QuickAccess from './CourseDetail/Partials/QuickAccess';

type Props = {
    course: Course & {
        progress?: {
            total_classes: number;
            completed_classes: number;
            percentage: number;
        };
    };
    last_class_viewed?: ClassData;
    first_class?: { id: number; title: string };
};

const CourseDetailContent: React.FC<Props> = ({
    course,
    last_class_viewed,
    first_class,
}) => {
    const { t } = useTranslation();
    const { route } = useRoutes();

    const breadcrumbItems = [
        {
            label: t('course.sectionDetail.courses'),
            href: route('home').toString(),
        },
        { label: course.title },
    ];

    const showProgress = course.is_enrolled;
    const showAma = course.has_ama_series;
    const showInfoRow = showProgress || showAma;

    return (
        <AppLayout>
            <PageTitle title={course.title} />
            <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
                {/* Breadcrumbs */}
                <div className="mb-6">
                    <Breadcrumbs items={breadcrumbItems} />
                </div>

                {!course.is_enrolled && (
                    <AlertBanner
                        variant="warning"
                        title={t('course.notEnrolledBanner.heading')}
                        className="mb-6"
                    >
                        {t('course.notEnrolledBanner.message')}
                    </AlertBanner>
                )}

                {/* Course Hero */}
                <CourseHeader
                    course={course}
                    lastClassViewedId={last_class_viewed?.id}
                    firstClassId={first_class?.id}
                />

                {/* Progress + AMA side by side */}
                {showInfoRow && (
                    <div
                        className={`mb-8 grid gap-4 ${showProgress && showAma ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}
                    >
                        {showProgress && (
                            <ProgressCard
                                progressPercentage={
                                    course.progress?.percentage || 0
                                }
                            />
                        )}
                        {showAma && (
                            <AmaSessionsCard courseSlug={course.slug} />
                        )}
                    </div>
                )}

                {/* Módulos grid */}
                <QuickAccess course={course} />
            </div>
        </AppLayout>
    );
};

const CourseDetail: React.FC<Props> = ({
    course,
    last_class_viewed,
    first_class,
}) => {
    return (
        <ThemeWrapper>
            <CourseDetailContent
                course={course}
                last_class_viewed={last_class_viewed}
                first_class={first_class}
            />
        </ThemeWrapper>
    );
};

export default CourseDetail;
